import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { type EventOrigin } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useIsolationVisibility } from "components/window/core/objectTree/hooks/useIsolationVisibility";
import { type BulkAction, createBulkActions } from "components/window/core/objectTree/utils/bulkActions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlightCollections } from "contexts/highlightCollections";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket, useSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import {
    selectDefaultVisibility,
    selectMainObject,
    selectMainObjects,
    selectPreIsolationVisibility,
} from "features/render";
import { useToggleXRayMode } from "features/render/hooks/useToggleXRayMode";
import { selectionIdIsHidden } from "features/render/sheetIsolationVisibility";
import { countModelGeometryIds } from "features/render/sheetSceneSelection";
import { ObjectVisibility } from "features/render/types";
import { usePdfObjectIds } from "hooks/useObjectTreeHidden";
import { unselectAll } from "utils/objectActions";
import { type IdList } from "utils/selectionSet";

export type SelectionActions = {
    /** `mainObjects` when populated (canvas, box select, tree), else the painted geometry ids. */
    selectedCount: number;
    /** Model objects only. Sheet ids are left out. */
    modelSelectedCount: number;
    highlightedIds: IdList;
    mainObjects: number[];
    hide: BulkAction | undefined;
    isolate: BulkAction | undefined;
    isolateXray: BulkAction | undefined;
    zoomToSelection: BulkAction | undefined;
    /** Mirrors the hide action's label rule so tooltips agree with the click. */
    areAllHidden: boolean;
    /** X-ray is on, whether from the toolbar toggle or the isolation path. */
    isXrayActive: boolean;
    /** Exits bare toolbar X-ray too, which `isolateXray.onClick` alone would re-isolate instead. */
    toggleXray: () => void;
    deselectAll: () => void;
    /** Deselect All is off in any X-ray mode, however X-ray was entered. */
    deselectAllDisabled: boolean;
};

/**
 * The actions the SelectionBar offers for the current selection. One definition so every trigger
 * (SelectionBar buttons, SpaceMouse commands) hides, isolates, and deselects the same way.
 */
export function useSelectionActions(origin: EventOrigin): SelectionActions {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();

    const highlighted = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const hidden = useHidden();
    const dispatchHidden = useDispatchHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const isolationVisibility = useIsolationVisibility();
    // isolationVisibility.active replaces the standalone selectPlainIsolationActive selector —
    // both come from the same Redux slice value.
    const plainIsolationActive = isolationVisibility.active;
    const selectionBasket = useSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const toggleXRayMode = useToggleXRayMode();

    const pdfObjectIds = usePdfObjectIds();
    const selectedIds = mainObjects.length > 0 ? mainObjects : highlighted.idArr;
    const selectedCount = selectedIds.length;
    const modelSelectedCount = countModelGeometryIds(selectedIds, pdfObjectIds);
    const isXrayActive = defaultVisibility === ObjectVisibility.Xray;

    const actions = useMemo(
        () =>
            createBulkActions(selectedCount, highlighted.idArr, {
                t,
                dispatch,
                store,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
                // Pass isolation visibility so the hide/unhide action correctly
                // handles objects that are invisible because they're outside the
                // isolation set (not just objects in hidden.ids).
                isolationVisibility,
                geometryIds: highlighted.idArr,
                pdfObjectIds,
                origin,
            }),
        [
            selectedCount,
            highlighted.idArr,
            t,
            dispatch,
            store,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            hidden.ids,
            hidden.lastHiddenIds,
            isolationVisibility,
            pdfObjectIds,
            origin,
        ],
    );

    const hide = actions.find((a) => a.id === "hide-selected");
    const isolate = actions.find((a) => a.id === "isolate");
    const isolateXray = actions.find((a) => a.id === "isolate-xray");
    const zoomToSelection = actions.find((a) => a.id === "zoom-to-selection");

    // When Xray was entered via the bottom-toolbar toggle (preIsolationVisibility is undefined),
    // the bulk-action onClick would re-isolate rather than exit. In that case, call toggleXRayMode
    // directly so the button correctly exits bare toolbar-Xray.
    const toggleXray = useCallback(() => {
        if (isXrayActive && preIsolationVisibility === undefined) {
            toggleXRayMode();
        } else {
            isolateXray?.onClick();
        }
    }, [isXrayActive, preIsolationVisibility, isolateXray, toggleXRayMode]);

    // Mirror createBulkActions "areAllHidden" logic so the tooltip label agrees with the click handler.
    // Must account for isolation mode: in isolation, objects are invisible because they're absent
    // from isolationVisibleIds (keepIds minus hidden.ids), not because they're in hidden.ids.
    const isolationVisibleIds = isolationVisibility.visibleIds;
    const areAllHidden =
        highlighted.idArr.length > 0 &&
        highlighted.idArr.every((id) => selectionIdIsHidden(id, hidden.ids, isolationVisibleIds, pdfObjectIds));

    const deselectAll = useCallback(() => {
        unselectAll({
            dispatch,
            dispatchHighlighted,
            // During plain isolation, TransparentSelectionBase is managed by
            // useUpdateTransparentSelectionBase via version-tracked snapshotIds.
            // Calling clearAll() here clears TransparentSelectionBase but the
            // version doesn't change, so the hook won't restore it — making isolated
            // objects go transparent. Skip clearAll when isolation is active.
            dispatchHighlightCollections: plainIsolationActive ? undefined : dispatchHighlightCollections,
            dispatchSelectionBasket,
            currentHighlightedIds: highlighted.idArr,
            currentMainObject: mainObject,
            currentMainObjects: mainObjects,
            currentSelectionBasketIds: selectionBasket.ids,
        });
        // TODO: add a dedicated analytics event for deselect-all once it exists in
        // the Amplitude Key Catalog. See src/apis/procore/analytics/events.ts.
    }, [
        dispatch,
        dispatchHighlightCollections,
        dispatchHighlighted,
        dispatchSelectionBasket,
        highlighted.idArr,
        mainObject,
        mainObjects,
        plainIsolationActive,
        selectionBasket.ids,
    ]);

    return {
        selectedCount,
        modelSelectedCount,
        highlightedIds: highlighted.idArr,
        mainObjects,
        hide,
        isolate,
        isolateXray,
        zoomToSelection,
        areAllHidden,
        isXrayActive,
        toggleXray,
        deselectAll,
        deselectAllDisabled: isXrayActive,
    };
}
